import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, CheckCircle2, AlertCircle, Loader2, Car, ChevronDown, Printer } from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import PdfReferenceNoteModal from '../components/PdfReferenceNoteModal';
import { useAuth } from '../context/AuthContext';
import { createVehicleReport, extractVehicleInfo, VehicleReportResponse, getErrorMessage, generatePdfReport, BulkVinItem, BulkVehicleReportResponse, createBulkVehicleReports, generateBulkPdfReport, fetchNmvtisPricing, fetchPdfForViewing } from '../services/vehicleReportService';
import { validateVINQuick, formatVehicleInfo, getVINErrorMessage, VINDecodeResult } from '../services/vinService';
import { getDashboardSettings } from '../services/settingsService';
import BulkVINInput from '../components/BulkVINInput';
import BulkReportResults from '../components/BulkReportResults';

// Session storage keys for persisting search results
const NMVTIS_SESSION_KEYS = {
  VIN: 'nmvtis_search_vin',
  REPORT_DATA: 'nmvtis_search_report_data',
  VIN_DECODE_RESULT: 'nmvtis_search_vin_decode_result',
  VIN_VALIDATION_ERROR: 'nmvtis_search_vin_validation_error',
  MODE: 'nmvtis_search_mode',
  BULK_RESULTS: 'nmvtis_bulk_results',
};

// Helper functions for session storage
const loadFromSession = <T,>(key: string): T | null => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveToSession = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session storage might be full or disabled
  }
};

const clearNMVTISSession = (): void => {
  Object.values(NMVTIS_SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

const NMVTISRecord: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoading } = useAuth();

  // Redirect if user doesn't have NMVTIS access
  useEffect(() => {
    if (isLoading) return;
    if (user?.canAccessNmvtis === false) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Initialize state from sessionStorage if available
  const [vin, setVin] = useState(() => loadFromSession<string>(NMVTIS_SESSION_KEYS.VIN) || '');
  const [isSearching, setIsSearching] = useState(false);
  const [reportData, setReportData] = useState<VehicleReportResponse | null>(() =>
    loadFromSession<VehicleReportResponse>(NMVTIS_SESSION_KEYS.REPORT_DATA)
  );
  const [error, setError] = useState('');
  const [isValidatingVIN, setIsValidatingVIN] = useState(false);
  const [vinDecodeResult, setVinDecodeResult] = useState<VINDecodeResult | null>(() =>
    loadFromSession<VINDecodeResult>(NMVTIS_SESSION_KEYS.VIN_DECODE_RESULT)
  );
  const [vinValidationError, setVinValidationError] = useState<string>(() =>
    loadFromSession<string>(NMVTIS_SESSION_KEYS.VIN_VALIDATION_ERROR) || ''
  );
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string>('');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPricing, setShowPricing] = useState(true);
  const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(true);
  const [nmvtisCostPerVin, setNmvtisCostPerVin] = useState<number | null>(null);

  // Bulk mode state
  const [mode, setMode] = useState<'single' | 'bulk'>(() =>
    loadFromSession<'single' | 'bulk'>(NMVTIS_SESSION_KEYS.MODE) || 'single'
  );
  const [bulkResults, setBulkResults] = useState<BulkVehicleReportResponse | null>(() =>
    loadFromSession<BulkVehicleReportResponse>(NMVTIS_SESSION_KEYS.BULK_RESULTS)
  );
  const [isBulkSearching, setIsBulkSearching] = useState(false);
  const [isGeneratingBulkPdf, setIsGeneratingBulkPdf] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // Embedded PDF preview state (mirrors MotorVehicleRecord pattern)
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string>('');

  // Persist state changes to sessionStorage
  useEffect(() => {
    saveToSession(NMVTIS_SESSION_KEYS.VIN, vin);
  }, [vin]);

  useEffect(() => {
    if (reportData) {
      saveToSession(NMVTIS_SESSION_KEYS.REPORT_DATA, reportData);
    }
  }, [reportData]);

  useEffect(() => {
    if (vinDecodeResult) {
      saveToSession(NMVTIS_SESSION_KEYS.VIN_DECODE_RESULT, vinDecodeResult);
    }
  }, [vinDecodeResult]);

  useEffect(() => {
    saveToSession(NMVTIS_SESSION_KEYS.VIN_VALIDATION_ERROR, vinValidationError);
  }, [vinValidationError]);

  // Persist bulk mode state
  useEffect(() => {
    saveToSession(NMVTIS_SESSION_KEYS.MODE, mode);
  }, [mode]);

  useEffect(() => {
    if (bulkResults) {
      saveToSession(NMVTIS_SESSION_KEYS.BULK_RESULTS, bulkResults);
    }
  }, [bulkResults]);

  // Fetch dashboard settings
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getDashboardSettings();
      setShowPricing(settings.showPricing);
    };
    fetchSettings();
  }, []);

  // Fetch NMVTIS pricing on mount
  useEffect(() => {
    let cancelled = false;
    const loadPricing = async () => {
      try {
        const pricing = await fetchNmvtisPricing();
        if (!cancelled) setNmvtisCostPerVin(pricing.costPerVin);
      } catch {
        if (!cancelled) setNmvtisCostPerVin(null);
      }
    };
    loadPricing();
    return () => { cancelled = true; };
  }, []);

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Redirect if not authenticated (only after loading completes)
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Pre-fill VIN from URL query parameter
  useEffect(() => {
    const vinParam = searchParams.get('vin');
    if (vinParam && vinParam.length >= 5) {
      const cleanVin = vinParam.replace(/\s/g, '').toUpperCase();
      setVin(cleanVin);
      handleVINValidation(cleanVin);
    }
  }, [searchParams]);

  // Auto-fetch the embedded NMVTIS PDF when a single-mode search yields records.
  // Mirrors the MotorVehicleRecord pattern so the on-page sections from "Report Summary"
  // down are replaced by the actual generated PDF.
  React.useEffect(() => {
    if (mode !== 'single') return;
    if (!reportData?.data || !vin) return;

    const vehicleInfo = extractVehicleInfo(reportData.data, reportData.stolenRecords);
    if (!vehicleInfo?.hasRecords) return;

    let cancelled = false;
    let createdUrl: string | null = null;

    setPdfPreviewLoading(true);
    setPdfPreviewError('');

    fetchPdfForViewing(vin, reportData.reportId)
      .then((url) => {
        if (cancelled) {
          window.URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setPdfPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setPdfPreviewError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setPdfPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) {
        window.URL.revokeObjectURL(createdUrl);
      }
    };
  }, [mode, vin, reportData]);

  // Reset the embedded PDF when the user clears results or switches modes.
  React.useEffect(() => {
    if (mode !== 'single' || !reportData) {
      setPdfPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
      setPdfPreviewError('');
    }
  }, [mode, reportData]);

  // Handle VIN validation - quick validation first for instant preview, then full decode in background
  const handleVINValidation = async (vinValue: string) => {
    // Clear previous validation results
    setVinValidationError('');
    setVinDecodeResult(null);

    // Skip validation if VIN is empty or too short (older VINs can be 5+ characters)
    if (!vinValue || vinValue.length < 5) {
      return;
    }

    // Basic format validation - check for invalid characters (I, O, Q not allowed in VINs)
    const invalidChars = /[IOQ]/i;
    if (invalidChars.test(vinValue)) {
      setVinValidationError('Invalid VIN format. VIN cannot contain I, O, or Q.');
      return;
    }

    // Step 1: Quick validation for instant preview (local decoder, no API calls)
    setIsValidatingVIN(true);
    try {
      const quickResponse = await validateVINQuick(vinValue);

      // Set decode result if available for instant preview
      if (quickResponse.decodeResult) {
        setVinDecodeResult(quickResponse.decodeResult);
      }

      // Set validation error if VIN has warnings
      if (!quickResponse.isValid && quickResponse.errorCode) {
        setVinValidationError(getVINErrorMessage(quickResponse.errorCode));
        setIsValidatingVIN(false);
        return;
      }

      setIsValidatingVIN(false);

      // Quick validation is sufficient for NMVTIS - no background DataOne call
      // (DataOne calls are billable and not needed for NMVTIS workflow)
    } catch (err) {
      console.error('VIN validation error:', err);
      setVinValidationError('Unable to validate VIN. Please try again.');
      setIsValidatingVIN(false);
    }
  };

  // Handle VIN input change
  const handleVINChange = (value: string) => {
    const upperValue = value.replace(/\s/g, '').toUpperCase();
    setVin(upperValue);

    // Clear validation errors when user starts typing
    if (vinValidationError) {
      setVinValidationError('');
    }

    // Auto-validate when VIN reaches 17 characters (standard length)
    if (upperValue.length === 17) {
      handleVINValidation(upperValue);
    } else if (upperValue.length < 5) {
      // Clear decode result if VIN is too short
      setVinDecodeResult(null);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!vin || vin.length < 5) {
      setError('Please enter a valid VIN (minimum 5 characters)');
      return;
    }

    setError('');
    setIsSearching(true);
    setReportData(null);

    try {
      const startTime = Date.now();
      const response = await createVehicleReport(vin);
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      // Store response time for display
      (response as any).responseTime = responseTime;

      setReportData(response);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClear = () => {
    setVin('');
    setReportData(null);
    setError('');
    setVinValidationError('');
    setVinDecodeResult(null);
    setPdfError('');
    // Clear sessionStorage when user explicitly clears
    clearNMVTISSession();
  };

  // Handle bulk search submission
  const handleBulkSearch = async (items: BulkVinItem[]) => {
    setBulkError('');
    setIsBulkSearching(true);
    setBulkResults(null);

    try {
      const response = await createBulkVehicleReports(items);
      setBulkResults(response);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setBulkError(errorMessage);
    } finally {
      setIsBulkSearching(false);
    }
  };

  // Handle bulk PDF generation
  const handleGenerateBulkPdf = async (showBrandChecklist: boolean = false) => {
    if (!bulkResults || bulkResults.totalSucceeded === 0) {
      setBulkError('No successful reports to generate PDF');
      return;
    }

    setBulkError('');
    setIsGeneratingBulkPdf(true);

    try {
      const requestedBy = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.email || 'Unknown User';

      // Extract successful items with their reference notes
      const successfulItems: BulkVinItem[] = bulkResults.results
        .filter(r => r.success)
        .map(r => ({
          vin: r.vin,
          referenceNote: r.referenceNote,
        }));

      await generateBulkPdfReport({
        items: successfulItems,
        requestedBy,
        showBrandChecklist,
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setBulkError(errorMessage);
    } finally {
      setIsGeneratingBulkPdf(false);
    }
  };

  // Handle mode change
  const handleModeChange = (newMode: 'single' | 'bulk') => {
    setMode(newMode);
    // Clear errors when switching modes
    setError('');
    setBulkError('');
  };

  // Clear bulk results
  const handleClearBulk = () => {
    setBulkResults(null);
    setBulkError('');
    sessionStorage.removeItem(NMVTIS_SESSION_KEYS.BULK_RESULTS);
  };

  const handleGeneratePdf = async (referenceNote: string, showBrandChecklist?: boolean) => {
    if (!vin) {
      setPdfError('No VIN available for PDF generation');
      setShowPdfModal(false);
      return;
    }

    setPdfError('');
    setIsGeneratingPdf(true);

    try {
      const requestedBy = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.email || 'Unknown User';

      await generatePdfReport({
        vin,
        reportId: reportData?.reportId,
        requestedBy,
        referenceNote: referenceNote.trim() || undefined,
        showBrandChecklist: showBrandChecklist || false,
      });

      // Close modal on success
      setShowPdfModal(false);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setPdfError(errorMessage);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Subtle Brand Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.04)_0%,transparent_70%)] rounded-full"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.03)_0%,transparent_70%)] rounded-full"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="relative z-10 lg:ml-[220px] pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Page Header */}
          <div className="glass-card-strong p-6 rounded-xl mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#c41e3a] text-xs font-bold uppercase tracking-wider">Vehicle Intelligence Report</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-1">
                  Vehicle History Report
                </h1>
                <p className="text-[#64748b] text-sm">Comprehensive NMVTIS title and brand verification</p>
              </div>
              <div className="flex items-center gap-4 glass-card px-4 py-3 rounded-lg flex-shrink-0">
                {reportData && (
                  <>
                    <div className="text-right">
                      <div className="text-[10px] text-[#64748b] uppercase tracking-wider whitespace-nowrap">Report ID</div>
                      <div className="font-mono text-sm font-semibold text-[#0f172a] whitespace-nowrap">RPT-{reportData.reportId?.substring(0, 6).toUpperCase() || 'N/A'}</div>
                    </div>
                    <div className="w-px h-8 bg-black/10"></div>
                    <div className="text-right">
                      <div className="text-[10px] text-[#64748b] uppercase tracking-wider whitespace-nowrap">Generated</div>
                      <div className="text-sm font-medium text-[#0f172a] whitespace-nowrap">{new Date(reportData.fetchedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                    <div className="w-px h-8 bg-black/10"></div>
                  </>
                )}
                <img src="/nmvtis-logo.jpg" alt="NMVTIS" className="h-8 w-auto" />
              </div>
            </div>
          </div>

          {/* Mode Toggle Tabs */}
          <div data-tour="nmvtis-search" className="glass-card-strong rounded-xl mb-6 overflow-hidden">
            <div className="flex border-b border-black/[0.06]">
              <button
                onClick={() => handleModeChange('single')}
                disabled={isSearching || isBulkSearching}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all ${
                  mode === 'single'
                    ? 'bg-white/80 text-[#0f172a] border-b-2 border-[#c41e3a]'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Single VIN
              </button>
              <button
                onClick={() => handleModeChange('bulk')}
                disabled={isSearching || isBulkSearching}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all ${
                  mode === 'bulk'
                    ? 'bg-white/80 text-[#0f172a] border-b-2 border-[#c41e3a]'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Bulk VINs
              </button>
            </div>

            {/* Single VIN Search */}
            {mode === 'single' && (
              <div className="p-4">
                <form onSubmit={handleSearch}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-black/[0.08]">
                      <Search className="w-5 h-5 text-[#94a3b8]" />
                      <input
                        type="text"
                        value={vin}
                        onChange={(e) => handleVINChange(e.target.value)}
                        placeholder="Enter VIN to generate a comprehensive vehicle report"
                        disabled={isSearching}
                        className="flex-1 bg-transparent text-[#0f172a] font-mono text-sm tracking-wider placeholder-[#94a3b8] focus:outline-none disabled:opacity-50"
                      />
                      <div>
                        {isSearching || isValidatingVIN ? (
                          <Loader2 className="w-5 h-5 text-[#c41e3a] animate-spin" />
                        ) : vinDecodeResult && !vinValidationError ? (
                          <CheckCircle2 className="w-5 h-5 text-[#16a34a]" />
                        ) : vinValidationError ? (
                          <AlertCircle className="w-5 h-5 text-[#d97706]" />
                        ) : null}
                      </div>
                    </div>
                    {showPricing && nmvtisCostPerVin !== null && (
                      <span className="badge-success px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
                        {nmvtisCostPerVin === 0
                          ? 'No Charge'
                          : `Est. $${nmvtisCostPerVin.toFixed(2)}`}
                      </span>
                    )}
                    <button
                      type="submit"
                      disabled={isSearching || !vin}
                      className="btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearching ? 'Searching...' : 'Search VIN'}
                    </button>
                    {(reportData || vin) && (
                      <button
                        type="button"
                        onClick={handleClear}
                        disabled={isSearching}
                        className="btn-secondary px-5 py-3 disabled:opacity-50"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* Bulk VIN Input */}
            {mode === 'bulk' && (
              <div className="p-4">
                <BulkVINInput
                  onSubmit={handleBulkSearch}
                  disabled={isBulkSearching}
                  maxVins={50}
                  costPerVin={showPricing ? nmvtisCostPerVin : null}
                />
              </div>
            )}
          </div>

          {/* Single mode error */}
          {mode === 'single' && error && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {/* Bulk mode error */}
          {mode === 'bulk' && bulkError && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{bulkError}</p>
            </div>
          )}

          {mode === 'single' && vinValidationError && (
            <div className="mb-6 p-4 bg-[#d97706]/10 border border-[#d97706]/30 rounded-xl text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#d97706]" />
                <div>
                  <p className="font-medium text-[#d97706]">{vinValidationError}</p>
                  <p className="text-xs text-[#92400e] mt-1">
                    You can still search this VIN. NMVTIS may return data with exceptions.
                  </p>
                </div>
              </div>
            </div>
          )}

          {mode === 'single' && vinDecodeResult && !vinValidationError && (
            <div className="mb-6 glass-card p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#c41e3a]/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#0f172a]">{(() => {
                      const vinMake = vinDecodeResult.make;
                      const hasUnknownMake = !vinMake || vinMake.startsWith('Unknown');
                      if (hasUnknownMake && (reportData?.vehicleMake || reportData?.vehicleModel)) {
                        const year = vinDecodeResult.year || reportData?.vehicleYear || '';
                        const make = reportData?.vehicleMake || '';
                        const model = reportData?.vehicleModel || '';
                        return [year, make, model].filter(Boolean).join(' ');
                      }
                      return formatVehicleInfo(vinDecodeResult);
                    })()}</p>
                    <div className="flex items-center gap-3">
                      {vinDecodeResult.checkDigitValid && (
                        <span className="badge-success px-2 py-1 rounded text-xs font-medium">Check Digit Valid</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#64748b]">
                    <span>VIN: <span className="font-mono text-[#c41e3a]">{vin}</span></span>
                    {vinDecodeResult.bodyClass && <span>| {vinDecodeResult.bodyClass}</span>}
                    {vinDecodeResult.driveType && <span>| {vinDecodeResult.driveType}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Searching State */}
          {mode === 'bulk' && isBulkSearching && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin mx-auto mb-4" />
                <h2 className="text-xl text-[#0f172a] mb-2">Processing Bulk Request...</h2>
                <p className="text-[#64748b]">Querying NMVTIS for multiple vehicles</p>
              </div>
            </div>
          )}

          {/* Bulk Results */}
          {mode === 'bulk' && !isBulkSearching && bulkResults && (
            <div className="space-y-6">
              <BulkReportResults
                results={bulkResults}
                showPricing={showPricing}
                onGeneratePdf={handleGenerateBulkPdf}
                isGeneratingPdf={isGeneratingBulkPdf}
              />

              {/* Clear Bulk Results Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleClearBulk}
                  className="btn-secondary px-5 py-2 text-sm"
                >
                  Clear Results
                </button>
              </div>
            </div>
          )}

          {/* Single VIN Searching State */}
          {mode === 'single' && isSearching && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin mx-auto mb-4" />
                <h2 className="text-xl text-[#0f172a] mb-2">Searching...</h2>
                <p className="text-[#64748b]">Querying 50+ databases</p>
              </div>
            </div>
          )}

          {/* Single VIN Results State */}
          {mode === 'single' && !isSearching && reportData && (() => {
            const vehicleInfo = extractVehicleInfo(reportData.data, reportData.stolenRecords);
            if (!vehicleInfo) return null;

            // Check if NMVTIS returned no actual records (empty titles and brands)
            if (!vehicleInfo.hasRecords) {
              return (
                <div className="space-y-6">
                  {/* No Records Found Message */}
                  <div className="p-6 bg-[#d97706]/10 border border-[#d97706]/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-[#d97706] flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-bold text-[#92400e]">No NMVTIS Records Found</h3>
                        <p className="text-sm text-[#92400e]/80 mt-2">
                          The National Motor Vehicle Title Information System (NMVTIS) does not have any title or brand records for this VIN.
                        </p>
                        <p className="text-sm text-[#92400e]/80 mt-2">
                          This could mean:
                        </p>
                        <ul className="text-sm text-[#92400e]/80 mt-1 ml-4 list-disc">
                          <li>The vehicle has never been titled in a participating state</li>
                          <li>Records are not yet available in the NMVTIS system</li>
                          <li>The vehicle may be too new or imported recently</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Exceptions Section - show if NMVTIS returned any exception messages */}
                  {vehicleInfo.exceptions.length > 0 && (
                    <div className="p-6 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl">
                      <div className="flex items-center gap-2 mb-4">
                        <AlertCircle className="w-5 h-5 text-[#dc2626]" />
                        <h3 className="text-sm font-bold text-[#dc2626]">NMVTIS Exceptions ({vehicleInfo.exceptions.length})</h3>
                      </div>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {vehicleInfo.exceptions.map((exception, i) => (
                          <div key={i} className="text-xs">
                            <span className="text-[#dc2626] font-semibold">{exception.code}</span>
                            <span className="text-[#64748b] ml-2">{exception.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* NMVTIS Verification Banner */}
                  <div className="glass-card p-6 rounded-xl">
                    <div className="flex items-center justify-center gap-4">
                      <img src="/nmvtis-logo.jpg" alt="NMVTIS" className="h-16 w-auto rounded" />
                      <div className="flex flex-col">
                        <div className="text-sm font-bold text-[#0f172a] mb-1">Verified by NMVTIS</div>
                        <div className="text-xs text-[#64748b]">All State Title Service Inc</div>
                        <div className="text-xs text-[#64748b]">Approved NMVTIS Data Provider</div>
                      </div>
                    </div>
                  </div>

                  {/* PDF Generation Error */}
                  {pdfError && (
                    <div className="p-3 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-[#dc2626] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[#dc2626]">{pdfError}</p>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div className="space-y-6">
                {/* Embedded NMVTIS PDF preview - mirrors MotorVehicleRecord */}
                <div className="glass-card-strong rounded-xl overflow-hidden">
                  {pdfPreviewLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin mr-3" />
                      <span className="text-[#64748b] text-sm">Loading PDF preview...</span>
                    </div>
                  ) : pdfPreviewError ? (
                    <div className="flex items-center justify-center py-12 px-4">
                      <AlertCircle className="w-5 h-5 text-[#d97706] mr-2 flex-shrink-0" />
                      <span className="text-[#92400e] text-sm">{pdfPreviewError}</span>
                    </div>
                  ) : pdfPreviewUrl ? (
                    <iframe
                      src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                      title="NMVTIS Vehicle History PDF"
                      className="w-full border-0 bg-white"
                      style={{ height: 'min(1500px, 90vh)' }}
                    />
                  ) : null}
                </div>

                {/* PDF Generation Error (download flow) */}
                {pdfError && (
                  <div className="p-3 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-[#dc2626] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#dc2626]">{pdfError}</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

          {/* NMVTIS Consumer Access Product Disclaimer - Always visible */}
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="glass-card border border-[#d97706]/30 rounded-xl overflow-hidden">
              <button
                onClick={() => setIsDisclaimerExpanded(!isDisclaimerExpanded)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-[#d97706]/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/nmvtis-logo.jpg"
                    alt="NMVTIS"
                    className="h-8 w-auto rounded"
                  />
                  <h3 className="text-sm font-bold text-[#92400e]">NMVTIS Consumer Access Product Disclaimer</h3>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-[#92400e] transition-transform duration-200 ${isDisclaimerExpanded ? 'rotate-180' : ''}`}
                />
              </button>

              {isDisclaimerExpanded && (
                <div className="px-4 pb-4 space-y-4 text-sm text-[#475569] leading-relaxed">
                  <p>
                    The National Motor Vehicle Title Information System (NMVTIS) is an electronic system that contains
                    information on certain automobiles titled in the United States. NMVTIS is intended to serve as a
                    reliable source of title and brand history for automobiles, but it does not contain detailed
                    information regarding a vehicle's repair history.
                  </p>

                  <p>
                    All states, insurance companies, and junk and salvage yards are required by federal law to regularly
                    report information to NMVTIS. However, NMVTIS does not contain information on all motor vehicles in
                    the United States because some states are not yet providing their vehicle data to the system. Currently,
                    the data provided to NMVTIS by states is provided in a variety of time frames; while some states report
                    and update NMVTIS data in "real-time" (as title transactions occur), other states send updates less
                    frequently, such as once every 24 hours or within a period of days. AAMVA has no control over
                    accuracy/completeness of data and therefore is not liable for any concerns regarding quality.
                  </p>

                  <p>
                    Information on previous, significant vehicle damage may not be included in the system if the vehicle
                    was never determined by an insurance company (or other appropriate entity) to be a "total loss" or
                    branded by a state titling agency. Conversely, an insurance carrier may be required to report a
                    "total loss" even if the vehicle's titling-state has not determined the vehicle to be "salvage" or "junk."
                  </p>

                  <p>
                    A vehicle history report is <strong className="text-[#dc2626]">NOT</strong> a substitute for an independent vehicle inspection.
                    Before making a decision to purchase a vehicle, consumers are strongly encouraged to also obtain an
                    independent vehicle inspection to ensure the vehicle does not have hidden damage. The Approved NMVTIS
                    Data Providers (look for the NMVTIS logo) can include vehicle condition data from sources other than NMVTIS.
                  </p>

                  <div>
                    <p className="font-semibold text-[#0f172a] mb-2">
                      NMVTIS data INCLUDES (as available by those entities required to report to the System):
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-[#64748b]">
                      <li>Information from participating state motor vehicle titling agencies.</li>
                      <li>
                        Information on automobiles, buses, trucks, motorcycles, recreational vehicles, motor homes, and tractors.
                        NMVTIS may not currently include commercial vehicles if those vehicles are not included in a state's
                        primary database for title records (in some states, those vehicles are managed by a separate state agency),
                        although these records may be added at a later time.
                      </li>
                      <li>
                        Information on "brands" applied to vehicles provided by participating state motor vehicle titling agencies.
                        Brand types and definitions vary by state, but may provide useful information about the condition or prior
                        use of the vehicle.
                      </li>
                      <li>Most recent odometer reading in the state's title record.</li>
                      <li>
                        Information from insurance companies, and auto recyclers, including junk and salvage yards, that is required
                        by law to be reported to the system, beginning March 31, 2009. This information will include if the vehicle
                        was determined to be a "total loss" by an insurance carrier.
                      </li>
                      <li>
                        Information from junk and salvage yards receiving a "cash for clunker" vehicle traded-in under the Consumer
                        Assistance to Recycle and Save Act of 2009 (CARS) Program.
                      </li>
                    </ul>
                  </div>

                  <p className="pt-2 border-t border-[#d97706]/20">
                    Consumers are advised to visit <a href="https://vehiclehistory.bja.ojp.gov" target="_blank" rel="noopener noreferrer" className="text-[#c41e3a] hover:text-[#a01830] underline">vehiclehistory.bja.ojp.gov</a> for
                    details on how to interpret the information in the system and understand the meaning of various labels
                    applied to vehicles by the participating state motor vehicle titling agencies.
                  </p>

                  <p>
                    <a href="https://vehiclehistory.bja.ojp.gov/nmvtis_glossary" target="_blank" rel="noopener noreferrer" className="text-[#c41e3a] hover:text-[#a01830] underline font-semibold">Brand Glossary</a> -
                    View definitions of all vehicle brand types used by state motor vehicle titling agencies.
                  </p>

                  <p>
                    <a href="https://vehiclehistory.bja.ojp.gov/nmvtis_states" target="_blank" rel="noopener noreferrer" className="text-[#c41e3a] hover:text-[#a01830] underline font-semibold">State Participation Map</a> -
                    View which states are currently reporting data to NMVTIS.
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />

      {/* Floating PDF Button - Only for single VIN mode with results */}
      {mode === 'single' && reportData && !isSearching && (
        <button
          onClick={() => setShowPdfModal(true)}
          disabled={isGeneratingPdf}
          className="fixed bottom-8 right-8 px-6 py-4 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white font-semibold rounded-full shadow-[0_8px_32px_rgba(196,30,58,0.35)] transition-all flex items-center gap-2 disabled:from-[#94a3b8] disabled:to-[#64748b] disabled:shadow-none disabled:cursor-not-allowed z-50 hover:scale-105"
          title="Print PDF Report (with reference note)"
        >
          {isGeneratingPdf ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Printer className="w-5 h-5" />
              <span>Print PDF</span>
            </>
          )}
        </button>
      )}

      {/* PDF Reference Note Modal */}
      <PdfReferenceNoteModal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        onGenerate={handleGeneratePdf}
        isGenerating={isGeneratingPdf}
        showBrandChecklistOption={true}
      />
    </div>
  );
};

export default NMVTISRecord;
